import { useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../store/store';
import {
  fetchFormulary,
  fetchFormularyPage,
  searchFormulary,
  setSearchTerm,
  setSelectedCategories,
  setSelectedForms,
  setSelectedRoutes,
  setSelectedPharmacies,
  clearError,
  selectFormularyItems,
  selectFormularyLoading,
  selectFormularyError,
  selectSearchTerm,
  selectIsSearching,
  selectSelectedCategories,
  selectSelectedForms,
  selectSelectedRoutes,
  selectSelectedPharmacies,
  selectFormularyPagination,
  selectPageNext,
  selectPagePrevious
} from '../store/slices/formularySlice';
import { selectCurrentClinic, selectClinicsLoading } from '../store/slices/authSlice';
import type { FormularyListItem, FormularyQueryParams } from '../services/formularyService';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { MultiSelect } from '@/components/MultiSelect';
import PaginationControls from '@/components/PaginationControls';

const Catalogue = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Redux selectors
  const formularyItems = useSelector(selectFormularyItems);
  const loading = useSelector(selectFormularyLoading);
  const isSearching = useSelector(selectIsSearching);
  const error = useSelector(selectFormularyError);
  const searchTerm = useSelector(selectSearchTerm);
  const selectedCategories = useSelector(selectSelectedCategories);
  const selectedForms = useSelector(selectSelectedForms);
  const selectedRoutes = useSelector(selectSelectedRoutes);
  const selectedPharmacies = useSelector(selectSelectedPharmacies);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const paginationData = useSelector(selectFormularyPagination);
  const pageNext = useSelector(selectPageNext);
  const pagePrevious = useSelector(selectPagePrevious);

  // Local UI state - these aren't used for filtering anymore

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          dispatch(searchFormulary(searchTerm));
        }, 300); // 300ms debounce
      };
    })(),
    [dispatch]
  );

  // Fetch formulary on component mount and when clinic changes
  useEffect(() => {
    // GATE: Wait for clinic loading to complete
    if (clinicsLoading) {
      console.log('[Catalogue] Waiting for clinics to load...');
      return;
    }

    // GATE: Ensure we have clinic data with formularies href
    if (!currentClinic?.formularies?.href) {
      console.log('[Catalogue] No formularies href available yet');
      return;
    }

    // NOW SAFE: Make API call
    console.log('[Catalogue] Loading formulary from:', currentClinic.formularies.href);
    dispatch(fetchFormulary({}));
  }, [dispatch, currentClinic, clinicsLoading]);

  // Handle search term changes with server-side search
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleOrderClick = (itemId: string, pharmacy?: string) => {
    console.log('[Catalogue] Ordering item:', { itemId, pharmacy });
    // Always create orders directly (no type selection needed)
    const params = new URLSearchParams({ itemId });
    if (pharmacy) params.append('pharmacy', pharmacy);
    navigate(`/create-order?${params.toString()}`);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    if (!loading) {
      dispatch(fetchFormularyPage({ page }));
    }
  };

  const handleNextPage = () => {
    if (pageNext && !loading) {
      dispatch(fetchFormularyPage({ hateoasUrl: pageNext }));
    }
  };

  const handlePreviousPage = () => {
    if (pagePrevious && !loading) {
      dispatch(fetchFormularyPage({ hateoasUrl: pagePrevious }));
    }
  };


  // Helper function to parse medication string into names and dosages
  const parseMedicationString = (fullName: string): { names: string; dosages: string } => {
    if (!fullName) return { names: 'N/A', dosages: '' };

    // Split the string to find medication names (usually at the end after dosages)
    // Pattern: dosages come first, then medication names
    // Example: "2.5mg / 0.5mg / 10.0mg / 1.0ml 2.5mg 10ml Semaglutide/Cyanocobalamin/Lidocaine"
    
    // Look for medication names - they typically don't contain numbers, mg, ml, etc.
    const parts = fullName.split(/\s+/);
    const dosageParts: string[] = [];
    const nameParts: string[] = [];
    
    for (const part of parts) {
      // If part contains medication name patterns (letters, slashes, but no dosage units)
      if (part.match(/[A-Za-z]/) && !part.match(/\d+(mg|ml|g|mcg|iu|units?)\b/i) && !part.match(/^\d/)) {
        // This looks like a medication name part
        nameParts.push(part);
      } else if (part.match(/\d+(mg|ml|g|mcg|iu|units?)\b/i) || part.match(/^\d/) || part === '/') {
        // This looks like a dosage part
        dosageParts.push(part);
      }
    }
    
    // Join medication names and replace / with ·
    let names = nameParts.join(' ').replace(/\//g, ' · ').trim();
    
    // Join dosage parts and replace / with ·
    let dosages = dosageParts.join(' ').replace(/\s*\/\s*/g, ' · ').replace(/\s+/g, ' ').trim();
    
    // Fallback: if we couldn't separate cleanly, try a different approach
    if (!names || names === 'N/A') {
      // Look for parts that are likely medication names (contain letters, no numbers)
      const medicationPattern = /([A-Za-z][A-Za-z\/]*[A-Za-z])\s*$/;
      const match = fullName.match(medicationPattern);
      
      if (match) {
        names = match[1].replace(/\//g, ' · ');
        dosages = fullName.replace(match[0], '').replace(/\s*\/\s*/g, ' · ').trim();
      } else {
        // Last resort: just clean up the full name
        names = fullName.replace(/\//g, ' · ');
        dosages = '';
      }
    }
    
    return { 
      names: names || 'N/A', 
      dosages: dosages || '' 
    };
  };

  // Helper function to get formatted medication names
  const getMedicationNames = (item: FormularyListItem): string => {
    const { names } = parseMedicationString(item.name || '');
    return names;
  };

  // Helper function to get formatted dosage information
  const getMedicationDosages = (item: FormularyListItem): string => {
    const { dosages } = parseMedicationString(item.name || '');
    return dosages;
  };

  // Helper function to expand formulary items by pharmacy
  // Each item with multiple pharmacy prices becomes multiple cards
  interface ExpandedFormularyItem extends FormularyListItem {
    pharmacy: string;
    price: number;
  }

  const expandItemsByPharmacy = (items: FormularyListItem[]): ExpandedFormularyItem[] => {
    const expanded: ExpandedFormularyItem[] = [];
    
    items.forEach(item => {
      if (item.pricing && item.pricing.length > 0) {
        item.pricing.forEach(pricing => {
          expanded.push({
            ...item,
            pharmacy: pricing.pharmacy,
            price: pricing.price
          });
        });
      } else {
        // If no pricing, still show the item with N/A
        expanded.push({
          ...item,
          pharmacy: 'N/A',
          price: 0
        });
      }
    });
    
    // Sort by medication name, then by price
    return expanded.sort((a, b) => {
      const nameCompare = a.name.localeCompare(b.name);
      if (nameCompare !== 0) return nameCompare;
      return a.price - b.price;
    });
  };

  // Simple text formatting - replace underscores with spaces
  const formatBadgeText = (text: string): string => {
    return text?.replace(/_/g, ' ') || '';
  };

  // Expand formulary items by pharmacy for display
  const expandedItems = expandItemsByPharmacy(formularyItems);

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content - Asymmetric padding on desktop to account for sidebar while staying centered */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:pl-24 lg:pr-8 py-8">
        {/* Catalogue Content */}
        <div className="mb-6">
            

            {/* Search Bar */}
            <div className="relative mb-6">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
              <Input
                type="text"
                placeholder="Search catalogue..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 h-12 bg-gray-100 border-0 focus:ring-2 focus:ring-vitl-green/20 focus:border-vitl-green font-lato"
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <MultiSelect
                options={[
                  { value: "peptide", label: "Peptide" },
                  { value: "weight_loss", label: "Weight Loss" },
                  { value: "hormone", label: "Hormone" },
                  { value: "sexual_health", label: "Sexual Health" },
                  { value: "nootropic", label: "Nootropic" },
                  { value: "anti_aging", label: "Anti Aging" },
                  { value: "vitamin_replacement", label: "Vitamin Replacement" },
                  { value: "ldn", label: "LDN" },
                  { value: "sterile", label: "Sterile" },
                  { value: "topical_anesthetic", label: "Topical Anesthetic" }
                ]}
                selectedValues={selectedCategories}
                onSelectionChange={(values: string[]) => {
                  dispatch(setSelectedCategories(values));
                  const params: FormularyQueryParams = {};
                  if (searchTerm) params.search = [searchTerm];
                  if (values.length > 0) params.category = values;
                  if (selectedRoutes.length > 0) params.route = selectedRoutes;
                  if (selectedForms.length > 0) params.form = selectedForms;
                  if (selectedPharmacies.length > 0) params.pharmacy = selectedPharmacies;
                  dispatch(fetchFormulary(params));
                }}
                placeholder="Category"
              />

              <MultiSelect
                options={[
                  { value: "oral", label: "Oral" },
                  { value: "topical", label: "Topical" },
                  { value: "injection", label: "Injection" },
                  { value: "nasal_spray", label: "Nasal Spray" },
                  { value: "suppository", label: "Suppository" }
                ]}
                selectedValues={selectedRoutes}
                onSelectionChange={(values: string[]) => {
                  dispatch(setSelectedRoutes(values));
                  const params: FormularyQueryParams = {};
                  if (searchTerm) params.search = [searchTerm];
                  if (selectedCategories.length > 0) params.category = selectedCategories;
                  if (values.length > 0) params.route = values;
                  if (selectedForms.length > 0) params.form = selectedForms;
                  if (selectedPharmacies.length > 0) params.pharmacy = selectedPharmacies;
                  dispatch(fetchFormulary(params));
                }}
                placeholder="Route"
              />

              <MultiSelect
                options={[
                  { value: "injection", label: "Injection" },
                  { value: "capsule", label: "Capsule" },
                  { value: "cream", label: "Cream" },
                  { value: "troche", label: "Troche" },
                  { value: "nasal_spray", label: "Nasal Spray" },
                  { value: "tablet", label: "Tablet" },
                  { value: "drops", label: "Drops" },
                  { value: "solution", label: "Solution" }
                ]}
                selectedValues={selectedForms}
                onSelectionChange={(values: string[]) => {
                  dispatch(setSelectedForms(values));
                  const params: FormularyQueryParams = {};
                  if (searchTerm) params.search = [searchTerm];
                  if (selectedCategories.length > 0) params.category = selectedCategories;
                  if (selectedRoutes.length > 0) params.route = selectedRoutes;
                  if (values.length > 0) params.form = values;
                  if (selectedPharmacies.length > 0) params.pharmacy = selectedPharmacies;
                  dispatch(fetchFormulary(params));
                }}
                placeholder="Form"
              />

              <MultiSelect
                options={[
                  { value: "Beaker", label: "Beaker" },
                  { value: "Partell", label: "Partell" },
                  { value: "PerfectRx", label: "PerfectRx" },
                  { value: "Promise", label: "Promise" },
                  { value: "Striker", label: "Striker" },
                  { value: "Strive", label: "Strive" }
                ]}
                selectedValues={selectedPharmacies}
                onSelectionChange={(values: string[]) => {
                  dispatch(setSelectedPharmacies(values));
                  const params: FormularyQueryParams = {};
                  if (searchTerm) params.search = [searchTerm];
                  if (selectedCategories.length > 0) params.category = selectedCategories;
                  if (selectedRoutes.length > 0) params.route = selectedRoutes;
                  if (selectedForms.length > 0) params.form = selectedForms;
                  if (values.length > 0) params.pharmacy = values;
                  dispatch(fetchFormulary(params));
                }}
                placeholder="Pharmacy"
              />

            

              

              
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <p className="text-red-700 font-lato text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Loading State - show during clinic loading OR formulary loading */}
            {(loading || isSearching || clinicsLoading) && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
                <span className="ml-2 text-gray-600">
                  {isSearching ? 'Searching...' : 
                   clinicsLoading ? 'Loading clinic data...' : 
                   'Loading formulary...'}
                </span>
              </div>
            )}

            {/* Formulary Items */}
            {!loading && !isSearching && !clinicsLoading && (
              <div className="space-y-4">
                {expandedItems.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-vitl-dark/70 font-lato">No formulary items found matching your filters.</p>
                  </div>
                ) : (
                  expandedItems.map((item, index) => (
                    <Card key={`${item.id}-${item.pharmacy}-${index}`} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                      <CardContent className="p-3 sm:p-4 lg:p-6">
                        {/* Mobile & Tablet Layout (Stack vertically) */}
                        <div className="block lg:hidden space-y-3">
                          {/* Header Row */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-lato font-medium text-vitl-navy text-base sm:text-lg mb-1 leading-snug">
                                {getMedicationNames(item)}
                              </h3>
                              {getMedicationDosages(item) && (
                                <p className="font-lato text-vitl-dark/60 text-sm mb-1 leading-snug">
                                  {getMedicationDosages(item)}
                                </p>
                              )}
                              <div className="flex items-center gap-3 flex-wrap">
                                <div>
                                  <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-emerald-200 leading-none mb-1">Category</p>
                                  <span className="block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-lato rounded border border-emerald-200">
                                    {formatBadgeText(item.category)}
                                  </span>
                                </div>
                                <div>
                                  <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-indigo-200 leading-none mb-1">Route</p>
                                  <span className="block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-lato rounded border border-indigo-200">
                                    {formatBadgeText(item.route)}
                                  </span>
                                </div>
                                <div>
                                  <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-amber-200 leading-none mb-1">Form</p>
                                  <span className="block px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-lato rounded border border-amber-200">
                                    {formatBadgeText(item.form)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium text-xs px-3 py-1.5 ml-2 flex-shrink-0"
                              onClick={() => handleOrderClick(item.id, item.pharmacy)}
                            >
                              Order
                            </Button>
                          </div>

                          {/* Description */}
                          {item.description && (
                            <p className="text-vitl-dark/70 font-lato text-sm leading-relaxed line-clamp-2">
                              {item.description}
                            </p>
                          )}

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato mb-0.5">Pharmacy</p>
                              <p className="text-vitl-navy font-lato text-sm font-medium">{item.pharmacy}</p>
                            </div>
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato mb-0.5">Price</p>
                              <p className="text-vitl-navy font-lato text-sm font-medium">
                                {item.price > 0 ? `$${item.price.toFixed(2)}` : 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout (Original) */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                          <div className="lg:col-span-4">
                            <h3 className="font-lato font-medium text-vitl-navy text-lg mb-1 leading-snug">
                              {getMedicationNames(item)}
                            </h3>
                            {getMedicationDosages(item) && (
                              <p className="font-lato text-vitl-dark/60 text-sm mb-1 leading-snug">
                                {getMedicationDosages(item)}
                              </p>
                            )}
                            <p className="text-vitl-dark/70 font-lato text-xs line-clamp-2">{item.description || 'No description available'}</p>
                          </div>
                          
                          <div className="lg:col-span-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-emerald-200 leading-none mb-1">Category</p>
                                <span className="block px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-lato rounded border border-emerald-200">
                                  {formatBadgeText(item.category)}
                                </span>
                              </div>
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-indigo-200 leading-none mb-1">Route</p>
                                <span className="block px-2 py-1 bg-indigo-50 text-indigo-700 text-xs font-lato rounded border border-indigo-200">
                                  {formatBadgeText(item.route)}
                                </span>
                              </div>
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-amber-200 leading-none mb-1">Form</p>
                                <span className="block px-2 py-1 bg-amber-50 text-amber-700 text-xs font-lato rounded border border-amber-200">
                                  {formatBadgeText(item.form)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Pharmacy</p>
                              <p className="text-vitl-navy font-lato text-sm font-semibold">{item.pharmacy}</p>
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <div>
                              <p className="text-vitl-dark/50 text-xs font-lato">Price</p>
                              <p className="text-vitl-navy font-lato text-lg font-semibold">
                                {item.price > 0 ? `$${item.price.toFixed(2)}` : 'N/A'}
                              </p>
                            </div>
                          </div>

                          <div className="lg:col-span-1 flex items-center justify-end space-x-3">
                            <Button 
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
                              onClick={() => handleOrderClick(item.id, item.pharmacy)}
                            >
                              Order
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {expandedItems.length > 0 && !loading && !isSearching && !clinicsLoading && (
              <PaginationControls
                paginationData={paginationData}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                loading={loading || isSearching}
                className="mt-8"
              />
            )}
        </div>
      </main>
    </div>
  );
};

export default Catalogue;
